import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTimezone } from "../../../context/TimezoneContext";
import "./TimezoneSelector.css";
import { FaGlobe, FaChevronDown, FaCheck } from "react-icons/fa";

const TimezoneSelector = () => {
  const { timezone, setTimezone } = useTimezone();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const timezones = useMemo(() => {
    try {
      return Intl.supportedValuesOf("timeZone"); //INTL timezones
    } catch (e) {
      return ["UTC", "Asia/Kolkata", "America/New_York"];
    }
  }, []);

  const formattedTimezones = useMemo(() => {
    const now = new Date();

    return timezones.map((tz) => {
      const offset = Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "shortOffset",
      })
        .formatToParts(now)
        .find((p) => p.type === "timeZoneName")?.value;

      return {
        value: tz,
        label: `${offset || ""} ${tz}`,
      };
    });
  }, [timezones]);

  const filtered = useMemo(() => {
    if (!search.trim()) return formattedTimezones;

    return formattedTimezones.filter((t) =>
      t.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, formattedTimezones]);

  const selectedLabel =
    formattedTimezones.find((t) => t.value === timezone)?.label || timezone;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="timezone-container" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`timezone-select ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="timezone-select-left">
          <FaGlobe className="timezone-icon" />
          <span className="timezone-selected-text">{selectedLabel}</span>
        </span>

        <FaChevronDown className="timezone-chevron" />
      </button>

      {open && (
        <div className="timezone-dropdown">
          <div className="timezone-search-wrapper">
            <input
              autoFocus
              placeholder="Search timezone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="timezone-search"
            />
          </div>

          <div className="timezone-list-div" role="listbox">
            {filtered.length > 0 ? (
              filtered.map((tz) => (
                <button
                  type="button"
                  className={`timezone-option ${
                    timezone === tz.value ? "is-selected" : ""
                  }`}
                  key={tz.value}
                  role="option"
                  aria-selected={timezone === tz.value}
                  onClick={() => {
                    setTimezone(tz.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <span>{tz.label}</span>

                  {timezone === tz.value && (
                    <FaCheck className="timezone-check" />
                  )}
                </button>
              ))
            ) : (
              <div className="timezone-empty">No timezone found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelector;